import { FC } from 'react';

import { useLocalStorage } from '@dbt-labs/metadata-shared';
import { NotificationBanner, NotificationBannerProps } from '@dbt-labs/sourdough';

interface DateTimeDismissableNotificationBannerProps extends NotificationBannerProps {
  localStorageKey: string;
  deadlineDate: Date;
  /** Days before deadline when banner becomes urgent and cannot be dismissed (7 days) */
  urgentThresholdDays: number;
  /** Text to display in warning phase (e.g. "(Required Dec 1)") */
  warningIndicatorText: string;
  /** Days before deadline when warning text appears (30 days) */
  warningThresholdDays: number;
  /** Hours to hide after dismissal during non-urgent periods (24 hours) */
  temporaryDismissalHours: number;
}

interface DismissalData {
  dismissedAt: string;
  dismissed: boolean;
}

export const DateTimeDismissableNotificationBanner: FC<
  DateTimeDismissableNotificationBannerProps
> = ({
  localStorageKey,
  deadlineDate,
  urgentThresholdDays,
  warningIndicatorText: urgentIndicatorText,
  warningThresholdDays,
  temporaryDismissalHours,
  notification,
  ...props
}) => {
  const [dismissalData, setDismissalData] = useLocalStorage<DismissalData | null>(
    localStorageKey,
    (value: any) => {
      if (
        value &&
        typeof value === 'object' &&
        'dismissedAt' in value &&
        'dismissed' in value
      ) {
        return value as DismissalData;
      }
      return null;
    },
    null,
  );

  const now = new Date();
  const timeDiff = deadlineDate.getTime() - now.getTime();
  const daysUntilDeadline = Math.ceil(timeDiff / (1000 * 3600 * 24));

  const isInUrgentPhase = daysUntilDeadline <= urgentThresholdDays;
  const isInWarningPhase = daysUntilDeadline <= warningThresholdDays;

  const shouldShowBanner = () => {
    if (!dismissalData?.dismissed) {
      return true;
    }

    if (isInUrgentPhase) {
      return true;
    }

    const dismissedAt = new Date(dismissalData.dismissedAt);
    const dismissalExpiry = new Date(
      dismissedAt.getTime() + temporaryDismissalHours * 60 * 60 * 1000,
    );
    return now > dismissalExpiry;
  };

  if (!shouldShowBanner()) {
    return null;
  }

  const enhancedNotification = {
    ...notification,
    header: isInWarningPhase
      ? `${notification.header} ${urgentIndicatorText}`
      : notification.header,
  };

  const handleDismiss = () => {
    if (!isInUrgentPhase) {
      setDismissalData({
        dismissedAt: now.toISOString(),
        dismissed: true,
      });
    }
  };

  return (
    <NotificationBanner
      {...props}
      notification={enhancedNotification}
      onCloseClick={isInUrgentPhase ? undefined : handleDismiss}
    />
  );
};
